import React, { useState, useMemo, useEffect } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PriceChangeIcon from "@mui/icons-material/PriceChange";

import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";

import styles from "./AdvisoryDetailsTable.module.css";
import { axiosConfig } from "../../../../axios/axiosConfig";
import usePartnersWS from "../../../../hooks/usePartnersWS";
import { partnersWSManager } from "../../../../services/partnersWSManager";
import { calcLivePnl } from "../../../../utils/livePnl";

const ADVISORY_TABS = ["OPEN", "CLOSED", "CANCEL", "INACTIVE"];

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} || ${hh}:${min}`;
};

// ── Per-row component — own WS subscription ─────────────────────────────────
// Extracted so usePartnersWS hook can be called per row (hooks can't go inside .map())
const AdvisoryRow = ({
  item,
  index,
  isEdit,
  showLtp,
  showExitCols,
  showPnl,
  showCallType,
  expandedRows,
  toggleRow,
  handleEditBtn,
  handleCancelBtn,
  handleExitBtn,
  handleOpenAdvisoryRevisedModal,
}) => {
  // Subscribe to live LTP for this row's symbol
  const { ltp: wsLtp } = usePartnersWS({ symbol: item.symbol_name });
  const ltp = wsLtp ?? item.ltp;
  console.log(item.ltp, "api")
  const livePnl = calcLivePnl({
    entryPrice: item.averaging || item.entry_price,
    ltp,
    quantity: item.quantity ?? 1,
    signal: item.signal,
  });

  // For closed trades: calculate P&L using averaging (if present) and exit_price
  const closedPnl = showPnl
    ? calcLivePnl({
        entryPrice: item.averaging || item.entry_price,
        ltp: item.exit_price,
        quantity: item.quantity ?? 1,
        signal: item.signal,
      })
    : null;

  const isOpen = ["OPEN", "INACTIVE"].includes(item?.trade_status?.toUpperCase());
  const hasAccordion = item?.revised_stock_advisory?.length > 0;
  const isExpanded = !!expandedRows[item.id];

  return (
    <React.Fragment>
      <tr
        onClick={hasAccordion ? () => toggleRow(item.id) : undefined}
        style={{ cursor: hasAccordion ? "pointer" : "default" }}
      >
        <td>{index + 1}</td>
        <td>{item?.symbol_name}</td>
        {showCallType && <td>{item?.call_type || "—"}</td>}
        {showCallType && <td>{item?.call_type === "BID" ? item?.bid_type || "—" : "—"}</td>}
        <td>{item?.entry_price}</td>
        <td>{item?.target_price}</td>
        {showExitCols && <td>{item?.exit_price}</td>}
        <td>{item?.stop_loss}</td>

        {showLtp && (
          <td style={{ whiteSpace: "nowrap" }}>
            {ltp != null ? (
              <span style={{ fontWeight: 700, color: "#016551" }}>{ltp}</span>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </td>
        )}

        {showLtp && (
          <td style={{ whiteSpace: "nowrap" }}>
            {livePnl != null ? (
              <span style={{ fontWeight: 700, color: livePnl.isProfit ? "#16a34a" : "#ef4444" }}>
                ₹{livePnl.pnl}
              </span>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </td>
        )}

        {showLtp && (
          <td style={{ whiteSpace: "nowrap" }}>
            {livePnl != null ? (
              <span style={{ fontWeight: 700, color: livePnl.isProfit ? "#16a34a" : "#ef4444" }}>
                {livePnl.pnlPercent}%
              </span>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </td>
        )}

        <td>{fmtDate(item?.created_at)}</td>
        <td>{fmtDate(item?.modified_at)}</td>
        <td>{item?.advisory_type}</td>
        {showExitCols && <td>{fmtDate(item?.exit_price_time)}</td>}
        {showPnl && (
          <td style={{ fontWeight: 600, color: closedPnl ? (closedPnl.isProfit ? "#16a34a" : "#ef4444") : undefined }}>
            {closedPnl
              ? `${closedPnl.pnlPercent.toFixed(2)} %`
              : item?.profit_loss != null
                ? `${Number(item.profit_loss).toFixed(2)} %`
                : "--"}
          </td>
        )}
        {showExitCols && (
          <td>
            {item?.trade_close_in_days != null
              ? `${Number(item.trade_close_in_days).toFixed(0)} days`
              : "--"}
          </td>
        )}
        <td>
          {item?.file ? (
            <a
              className={styles.fileDownloadLink}
              href={item.file}
              target="_blank"
              rel="noreferrer"
              title={item?.file_name || "Download"}
            >
              <DownloadIcon sx={{ fontSize: 14 }} />
            </a>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
          )}
        </td>
        <td>{item?.averaging ?? "—"}</td>
        {!isEdit && !showExitCols && (
          <td onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
              <Tooltip title="Edit" placement="top">
                <button className={styles.editBtn} onClick={() => handleEditBtn(item)}>
                  <EditIcon sx={{ fontSize: 13 }} />
                </button>
              </Tooltip>
              <Tooltip title="Cancel Trade" placement="top">
                <button disabled={!isOpen} className={styles.cancelBtn} onClick={() => handleCancelBtn(item)}>
                  <CancelIcon sx={{ fontSize: 13 }} />
                </button>
              </Tooltip>
              <Tooltip title="Revise Advisory Entry Price" placement="top">
                <button disabled={!isOpen} className={styles.revisedBtn} onClick={() => handleOpenAdvisoryRevisedModal(item)}>
                  <PriceChangeIcon sx={{ fontSize: 13 }} />
                </button>
              </Tooltip>
              <Tooltip title="Exit Trade" placement="top">
                <button disabled={!isOpen} className={styles.exitBtn} onClick={() => handleExitBtn(item)}>
                  <ExitToAppIcon sx={{ fontSize: 13 }} />
                </button>
              </Tooltip>
            </div>
          </td>
        )}
        <td style={{ textAlign: "center", width: 24 }}>
          {hasAccordion && (
            <span style={{ fontSize: 10, color: "#016551", fontWeight: 700 }}>
              {isExpanded ? "▲" : "▼"}
            </span>
          )}
        </td>
        <td>{item?.user || "—"}</td>
        {showExitCols && !showPnl && <td>{item?.cancel_reason || "-"}</td>}
      </tr>

      {/* Accordion row — revised entry price chain */}
      {hasAccordion && isExpanded && (
        <tr>
          <td
            colSpan={99}
            style={{ padding: 0, background: "#f0fdf4", borderBottom: "2px solid #bbf7d0" }}
          >
            <div style={{ padding: "14px 28px", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div
                style={{
                  background: "#04343a",
                  borderRadius: 8,
                  padding: "8px 16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 90,
                }}
              >
                <span style={{ fontSize: 10, color: "#b8d4c4", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Entry
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginTop: 2 }}>
                  {item.entry_price}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0 }}>
                {item.revised_stock_advisory.map((entry, i) => (
                  <React.Fragment key={entry.id}>
                    <span style={{ color: "#016551", fontSize: 16, fontWeight: 700, padding: "0 6px" }}>→</span>
                    <div
                      style={{
                        background: "#fff",
                        border: "1.5px solid #bbf7d0",
                        borderRadius: 8,
                        padding: "6px 14px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: 90,
                      }}
                    >
                      <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Rev. {i + 1}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#016551", marginTop: 2 }}>
                        {entry.revised_price}
                      </span>
                      <span style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
                        {fmtDate(entry.created_at)}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const AdvisoryDetailsTable = ({
  advisoryDetailsList,
  handleEditBtn,
  handleCancelBtn,
  handleExitBtn,
  isEdit,
  refreshAdvisory,
}) => {
  const [activeTab, setActiveTabState] = useState(
    () => sessionStorage.getItem("advisory_active_tab") || "OPEN",
  );
  console.log(advisoryDetailsList, "advisoryDetailsList")
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    sessionStorage.setItem("advisory_active_tab", tab);
  };
  // OPEN and INACTIVE trades are still live: show LTP / actions, no exit columns
  const isLiveView = activeTab === "OPEN" || activeTab === "INACTIVE";
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const [advisoryRevisedModal, setAdvisoryRevisedModal] = useState({
    open: false,
    item: null,
  });
  const [advisoryRevisedPrice, setAdvisoryRevisedPrice] = useState("");
  const [advisoryRevisedLoading, setAdvisoryRevisedLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  // Websocket connection for live LTP updates
  useEffect(() => {
    const symbols = (advisoryDetailsList || [])
      .map((item) => item.symbol_name)
      .filter(Boolean);

    if (symbols.length > 0) partnersWSManager.bulkSubscribe(symbols);

    console.log("Subscribed to symbols", symbols);
  }, [advisoryDetailsList]);

  // Track WS connection status — [] so it runs once on mount only
  useEffect(() => {
    const unsubscribe = partnersWSManager.subscribeStatus(setWsConnected);
    return unsubscribe;
  }, []);

  const handleOpenAdvisoryRevisedModal = (item) => {
    setAdvisoryRevisedPrice("");
    setAdvisoryRevisedModal({ open: true, item });
  };

  const handleCloseAdvisoryRevisedModal = () => {
    setAdvisoryRevisedModal({ open: false, item: null });
    setAdvisoryRevisedPrice("");
  };

  const handleSubmitAdvisoryRevisedPrice = async () => {
    if (!advisoryRevisedPrice) return;
    try {
      setAdvisoryRevisedLoading(true);
      const response = await axiosConfig({
        method: "post",
        url: `/predictions/revised-advisory-entry-price`,
        data: {
          advisory_id: advisoryRevisedModal.item?.id,
          revised_price: advisoryRevisedPrice,
        },
      });
      toast.success(
        response?.data?.response || "Advisory entry price revised successfully",
      );
      handleCloseAdvisoryRevisedModal();
      refreshAdvisory?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to revise advisory entry price",
      );
    } finally {
      setAdvisoryRevisedLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    if (!advisoryDetailsList) return [];
    return advisoryDetailsList
      .filter((item) => {
        const status = item?.trade_status?.toUpperCase();
        if (activeTab === "OPEN") return status === "OPEN";
        if (activeTab === "CANCEL")
          return status === "CANCEL" || status === "CANCELLED";
        if (activeTab === "INACTIVE") return status === "INACTIVE";
        return status === "CLOSED" || status === "CLOSE";
      })
      .sort((a, b) => {
        const field = activeTab === "CLOSED" ? "exit_price_time" : "created_at";
        const da = a[field] ? new Date(a[field]).getTime() : 0;
        const db = b[field] ? new Date(b[field]).getTime() : 0;
        return db - da;
      });
  }, [advisoryDetailsList, activeTab]);

  const [csvLoading, setCsvLoading] = useState(false);

  const downloadCSV = async () => {
    try {
      setCsvLoading(true);
      const res = await axiosConfig({
        method: "get",
        url: "/predictions/stocks/advisory",
        data: {},
      });

      console.log("[CSV] raw response:", res?.data);
      const allData = res?.data?.response?.data || res?.data?.response || res?.data || [];
      const list = Array.isArray(allData) ? allData : [];
      console.log("[CSV] records count:", list.length);

      const columns = [
        { label: "Sr No", key: (_, i) => i + 1 },
        { label: "Symbol", key: "symbol_name" },
        { label: "Signal", key: "signal" },
        { label: "Entry Price", key: "entry_price" },
        { label: "Averaging", key: "averaging" },
        { label: "Target Price", key: "target_price" },
        { label: "Exit Price", key: "exit_price" },
        { label: "Stop Loss", key: "stop_loss" },
        { label: "Advisory Type", key: "advisory_type" },
        { label: "Trade Status", key: "trade_status" },
        { label: "Profit/Loss %", key: "profit_loss" },
        { label: "Close Days", key: "trade_close_in_days" },
        { label: "Created At", key: (item) => fmtDate(item.created_at) },
        { label: "Modified At", key: (item) => fmtDate(item.modified_at) },
        { label: "End Date", key: (item) => fmtDate(item.exit_price_time) },
        { label: "Cancel Reason", key: "cancel_reason" },
        { label: "User", key: "user" },
      ];

      const escape = (val) => {
        if (val == null || val === "") return "";
        const str = String(val);
        return str.includes(",") || str.includes('"') || str.includes("\n")
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      };

      const header = columns.map((c) => c.label).join(",");
      const rows = list.map((item, i) =>
        columns
          .map((c) => escape(typeof c.key === "function" ? c.key(item, i) : item[c.key]))
          .join(",")
      );

      const csv = [header, ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `advisory_all_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download CSV");
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div className={styles.AdvisoryDetailsTable}>
      {/* Section header */}
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          Stock Advisory History
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className={styles.tabGroup}>
            {ADVISORY_TABS.map((tab, i) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={styles.tabBtn}
                style={{
                  backgroundColor:
                    activeTab === tab ? "#016551" : "rgba(255,255,255,0.12)",
                  fontWeight: activeTab === tab ? 700 : 400,
                  borderRadius:
                    i === 0 ? "6px 0 0 6px" : i === ADVISORY_TABS.length - 1 ? "0 6px 6px 0" : "0",
                }}
              >
                {tab.charAt(0) + tab.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          <button
            onClick={downloadCSV}
            disabled={csvLoading}
            title="Download full advisory CSV"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 12px",
              background: csvLoading ? "#64748b" : "#016551",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: csvLoading ? "not-allowed" : "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <DownloadIcon sx={{ fontSize: 15 }} />
            {csvLoading ? "..." : "CSV"}
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.tableContainer}>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Symbol</th>
              {activeTab === "INACTIVE" && <th>Call Type</th>}
              {activeTab === "INACTIVE" && <th>Bid Type</th>}
              <th>Entry</th>
              <th>Target</th>
              {!isLiveView && <th>Exit</th>}
              <th>Stop Loss</th>
              {isLiveView && <th>LTP</th>}
              {isLiveView && <th>Live P&L</th>}
              {isLiveView && <th>Live P&L %</th>}
              <th>Created</th>
              <th>Modified</th>
              <th>Advisory Type</th>
              {!isLiveView && <th>End Date</th>}
              {activeTab === "CLOSED" && <th>Profit / Loss</th>}
              {!isLiveView && <th>Close Days</th>}
              <th>Download</th>
              <th>Average</th>
              {!isEdit && isLiveView && <th>Actions</th>}
              <th></th>
              <th>User</th>
              {activeTab === "CANCEL" && <th>Cancel Reason</th>}
            </tr>
          </thead>

          <tbody className={styles.tableBodyContainer}>
            {/* Each row is its own component so usePartnersWS hook can be called per row */}
            {filteredList?.map((item, index) => (
              <AdvisoryRow
                key={item.id}
                item={item}
                index={index}
                isEdit={isEdit}
                showLtp={isLiveView}
                showExitCols={!isLiveView}
                showPnl={activeTab === "CLOSED"}
                showCallType={activeTab === "INACTIVE"}
                expandedRows={expandedRows}
                toggleRow={toggleRow}
                handleEditBtn={handleEditBtn}
                handleCancelBtn={handleCancelBtn}
                handleExitBtn={handleExitBtn}
                handleOpenAdvisoryRevisedModal={handleOpenAdvisoryRevisedModal}
              />
            ))}
          </tbody>
        </table>
      </div>

      {advisoryRevisedModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleCloseAdvisoryRevisedModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px 32px",
              minWidth: 340,
              boxShadow: "0 8px 32px rgba(4,52,58,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 20px",
                color: "#04343a",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              Revise Advisory Entry Price
            </h3>

            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#64748b",
                  marginBottom: 4,
                  fontWeight: 600,
                }}
              >
                Symbol Name
              </label>
              <input
                value={advisoryRevisedModal.item?.symbol_name || ""}
                disabled
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: "border-box",
                  background: "#f8fafc",
                  color: "#1e293b",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#64748b",
                  marginBottom: 4,
                  fontWeight: 600,
                }}
              >
                Entry Price
              </label>
              <input
                value={advisoryRevisedModal.item?.entry_price || ""}
                disabled
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: "border-box",
                  background: "#f8fafc",
                  color: "#1e293b",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#64748b",
                  marginBottom: 4,
                  fontWeight: 600,
                }}
              >
                Revised Price
              </label>
              <input
                type="number"
                value={advisoryRevisedPrice}
                onChange={(e) => setAdvisoryRevisedPrice(e.target.value)}
                placeholder="Enter revised price"
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            <div
              style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
            >
              <button
                onClick={handleCloseAdvisoryRevisedModal}
                style={{
                  padding: "8px 20px",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAdvisoryRevisedPrice}
                disabled={advisoryRevisedLoading || !advisoryRevisedPrice}
                style={{
                  padding: "8px 20px",
                  borderRadius: 6,
                  border: "none",
                  background:
                    advisoryRevisedLoading || !advisoryRevisedPrice
                      ? "#94a3b8"
                      : "#016551",
                  color: "#fff",
                  cursor:
                    advisoryRevisedLoading || !advisoryRevisedPrice
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {advisoryRevisedLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvisoryDetailsTable;
